/**
 * Validated WidgetDefinition grammar + builder-form -> Cube wire-query transform.
 *
 * The WidgetBuilder edits a rich *builder form* (`WidgetDefinition`): `cube`,
 * `dimensions` as `{ field, label }` objects, `filters` with short operators
 * (`eq`/`neq`/...), a `dateRange`, `metrics`, and free-form
 * `widgetComponentProps`. None of that is Cube's query grammar.
 *
 * `cubeApi.load()` expects a `@cubejs-client/core` {@link Query}:
 * `dimensions: string[]`, `filters` with `equals`/`notEquals`/... + `values`,
 * `timeDimensions`, `order` — and NO `cube` / `dateRange` / `metrics`.
 *
 * Historically the builder shape was spread straight into `cubeApi.load()`,
 * which is a latent bug. This module owns the explicit, typed transform, plus a
 * structural `parseWidgetDefinition` that rejects malformed definitions with a
 * clear error (the precondition for LLM output validation + retry — ADR-051).
 */

import type {
  Query,
  Filter,
  TimeDimension,
  TimeDimensionBase,
  TimeDimensionComparison,
  QueryOrder,
  BinaryFilter,
  UnaryFilter,
  BinaryOperator,
  UnaryOperator
} from '@cubejs-client/core';
import type {
  WidgetDefinition,
  FilterItem,
  FilterOperator,
  DateRangeItem
} from './WidgetBuilder/types';
import { webWidgetTypes, mobileWidgetTypes, isDatalessWidgetType, isReadmodelWidgetType } from './types';
import { priorPeriodOf, resolveRelativeRange, type ResolvedDateRange } from './dateRange';

/** Thrown when an object does not conform to the WidgetDefinition grammar. */
export class WidgetDefinitionError extends Error {
  readonly errors: string[];
  constructor( errors: string[]) {
    super( `Invalid widget definition: ${errors.join( '; ' )}` );
    this.name = 'WidgetDefinitionError';
    this.errors = errors;
  }
}

// ---------------------------------------------------------------------------
// Builder -> Cube grammar mapping tables
// ---------------------------------------------------------------------------

/**
 * Builder operators mapped onto Cube's filter operators.
 *
 * The first block is the builder's own short forms (what the Filters section
 * offers). The second is PREFACTOR-grammar's identity block: Cube's native
 * operators, which a DOMAIN-authored definition (LLM / curated report) can carry
 * — `CubeFilterSchema` allows all eighteen — and which now map to themselves
 * instead of falling off the edge of the table into `undefined`.
 */
export const FILTER_OPERATOR_MAP: Record<FilterOperator, BinaryOperator | UnaryOperator> = {
  eq: 'equals',
  neq: 'notEquals',
  lt: 'lt',
  gt: 'gt',
  lte: 'lte',
  gte: 'gte',
  in: 'equals',
  nin: 'notEquals',
  exists: 'set',
  notExists: 'notSet',
  // Cube-native passthrough (domain-authored filters).
  equals: 'equals',
  notEquals: 'notEquals',
  contains: 'contains',
  notContains: 'notContains',
  startsWith: 'startsWith',
  endsWith: 'endsWith',
  set: 'set',
  notSet: 'notSet',
  inDateRange: 'inDateRange',
  notInDateRange: 'notInDateRange',
  beforeDate: 'beforeDate',
  beforeOrOnDate: 'beforeOrOnDate',
  afterDate: 'afterDate',
  afterOrOnDate: 'afterOrOnDate'
};

/** Builder operators that map onto Cube's value-less unary filters. */
const UNARY_BUILDER_OPERATORS: ReadonlySet<FilterOperator> = new Set([
  'exists', 'notExists', 'set', 'notSet'
]);

const VALID_FILTER_OPERATORS: ReadonlySet<string> = new Set( Object.keys( FILTER_OPERATOR_MAP ));
const VALID_WIDGET_TYPES: ReadonlySet<string> = new Set([ ...webWidgetTypes, ...mobileWidgetTypes ]);
const VALID_ORDER_DIRECTIONS: ReadonlySet<string> = new Set([ 'asc', 'desc' ]);

// ---------------------------------------------------------------------------
// Structural validation
// ---------------------------------------------------------------------------

const isRecord = ( value: unknown ): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray( value );

const isNonEmptyString = ( value: unknown ): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * Structurally validate an arbitrary value as a {@link WidgetDefinition}.
 *
 * Required: `id`, `name`, `cube`, a non-empty `measures[]`, and a known widget
 * `type`. Optional collections (`dimensions`, `filters`, `orderBy`,
 * `dateRange`) are validated shallowly when present. Throws
 * {@link WidgetDefinitionError} listing every problem found.
 */
export const parseWidgetDefinition = ( input: unknown ): WidgetDefinition => {
  const errors: string[] = [];

  if ( !isRecord( input )) {
    throw new WidgetDefinitionError(['definition must be an object']);
  }

  if ( !isNonEmptyString( input.id )) errors.push( 'id is required' );
  if ( !isNonEmptyString( input.name )) errors.push( 'name is required' );

  if ( !isNonEmptyString( input.type )) {
    errors.push( 'type is required' );
  } else if ( !VALID_WIDGET_TYPES.has( input.type )) {
    errors.push( `type "${input.type}" is not a supported widget type` );
  }

  // TV1-1930 — one of the front-end places that hard-code "a cube and at least
  // one measure". A dataless widget (`text`, `heading`, `spacer`) has no data
  // binding at all, and a readmodel widget (`count`, `alert`, `list`,
  // `progress` — TV1-2291 §6) binds via `collection`, not `cube`/`measures`.
  // Both are exempt from this precondition, exactly as `WebWidgetValidators.
  // validateCommon` exempts them — see `READMODEL_WIDGET_TYPES` in `./types`.
  // Everything below this point (dimensions/filters/orderBy/dateRange) is
  // already optional, so no further exception is needed.
  const isDataless = isDatalessWidgetType( input.type as string ) || isReadmodelWidgetType( input.type as string );

  if ( !isDataless ) {
    if ( !isNonEmptyString( input.cube )) errors.push( 'cube (data source) is required' );

    if ( !Array.isArray( input.measures ) || input.measures.length === 0 ) {
      errors.push( 'measures must contain at least one measure' );
    } else if ( !input.measures.every( isNonEmptyString )) {
      errors.push( 'measures must all be non-empty member references' );
    }
  }

  if ( input.dimensions !== undefined ) {
    if ( !Array.isArray( input.dimensions )) {
      errors.push( 'dimensions must be an array' );
    } else if ( !input.dimensions.every( d => isRecord( d ) && isNonEmptyString( d.field ))) {
      errors.push( 'each dimension must have a non-empty field' );
    }
  }

  if ( input.filters !== undefined ) {
    if ( !Array.isArray( input.filters )) {
      errors.push( 'filters must be an array' );
    } else {
      input.filters.forEach(( f, i ) => {
        if ( !isRecord( f ) || !isNonEmptyString( f.field )) {
          errors.push( `filter[${i}] must have a non-empty field` );
        } else if ( typeof f.operator !== 'string' || !VALID_FILTER_OPERATORS.has( f.operator )) {
          errors.push( `filter[${i}] has an unknown operator "${String( f.operator )}"` );
        }
      });
    }
  }

  if ( input.orderBy !== undefined ) {
    if ( !Array.isArray( input.orderBy )) {
      errors.push( 'orderBy must be an array' );
    } else {
      input.orderBy.forEach(( o, i ) => {
        if ( !isRecord( o ) || !isNonEmptyString( o.field )) {
          errors.push( `orderBy[${i}] must have a non-empty field` );
        } else if ( typeof o.direction !== 'string' || !VALID_ORDER_DIRECTIONS.has( o.direction )) {
          errors.push( `orderBy[${i}] direction must be "asc" or "desc"` );
        }
      });
    }
  }

  if ( input.dateRange !== undefined && input.dateRange !== null ) {
    if ( !isRecord( input.dateRange ) || !isNonEmptyString( input.dateRange.field )) {
      errors.push( 'dateRange must have a non-empty field' );
    } else if (
      input.dateRange.compare !== undefined &&
      typeof input.dateRange.compare !== 'boolean'
    ) {
      // TV1-1930 — `compare` opts the time dimension into Cube's
      // `compareDateRange`. Typed strictly here because a truthy non-boolean from
      // an LLM-authored definition would silently change the SHAPE of the fetched
      // data (a `{ current, prior }` tuple instead of a flat row array).
      errors.push( 'dateRange.compare must be a boolean' );
    }
  }

  if ( input.widgetComponentProps !== undefined && !isRecord( input.widgetComponentProps )) {
    errors.push( 'widgetComponentProps must be an object' );
  }

  if ( input.limit !== undefined && typeof input.limit !== 'number' ) errors.push( 'limit must be a number' );
  if ( input.offset !== undefined && typeof input.offset !== 'number' ) errors.push( 'offset must be a number' );

  if ( errors.length > 0 ) {
    throw new WidgetDefinitionError( errors );
  }

  return input as unknown as WidgetDefinition;
};

// ---------------------------------------------------------------------------
// Builder form -> Cube wire query
// ---------------------------------------------------------------------------

const builderFilterToCubeFilter = ( filter: FilterItem ): Filter => {
  const operator = FILTER_OPERATOR_MAP[filter.operator];

  if ( UNARY_BUILDER_OPERATORS.has( filter.operator )) {
    const unary: UnaryFilter = { member: filter.field, operator: operator as UnaryOperator };
    return unary;
  }

  // PREFACTOR-grammar — a multi-value `values[]` (which is what Cube and the
  // domain grammar actually speak) wins over the builder form's single `value`.
  // Reading `value` first would silently narrow `channel equals [ML, TN]` to
  // `channel equals [ML]` — a chart that looks right and is wrong.
  const values = filter.values?.length
    ? [...filter.values]
    : filter.value !== undefined && filter.value !== '' ? [filter.value] : [];

  const binary: BinaryFilter = {
    member: filter.field,
    operator: operator as BinaryOperator,
    values
  };
  return binary;
};

/**
 * Resolve the builder's date range into a CONCRETE `[ start, end ]` window.
 *
 * TV1-1930 — this is the fix for a pre-existing latent bug. The builder's
 * Relative Range select emits a token (`'1mo'`, `'3mo'`, …) and that token used
 * to be assigned **verbatim** to `timeDimension.dateRange`. **Cube cannot parse
 * `'1mo'`** (see `dateRange.ts` for the evidence, straight out of the shipped
 * `date-parser.js`), so every date-ranged widget built through the UI has been
 * sending Cube a range it either rejects or degenerates to a zero-width instant.
 *
 * Three cases, in priority order:
 *  1. explicit `start`/`end` (the builder's "custom range") — already concrete;
 *  2. a known relative token — resolved to a concrete window ending now;
 *  3. anything else — returned as-is (`string`), so a hand-authored or
 *     LLM-authored definition carrying a phrase Cube's own parser DOES
 *     understand ("last 30 days") keeps working exactly as it does today. We
 *     resolve what we know we broke and touch nothing else.
 */
export const resolveDateRangeWindow = (
  dateRange: DateRangeItem,
  now?: Date
): ResolvedDateRange | string | null => {
  if ( dateRange.start && dateRange.end ) return [ dateRange.start, dateRange.end ];
  if ( !dateRange.relativeRange ) return null;

  return resolveRelativeRange( dateRange.relativeRange, now ) ?? dateRange.relativeRange;
};

const builderDateRangeToTimeDimension = ( dateRange: DateRangeItem, now?: Date ): TimeDimension => {
  const timeDimension: TimeDimensionBase = { dimension: dateRange.field };

  if ( dateRange.granularity?.unit ) {
    timeDimension.granularity = dateRange.granularity.unit;
  }

  const window = resolveDateRangeWindow( dateRange, now );
  if ( !window ) return timeDimension;

  // TV1-1930 — period-over-period compare. Cube expresses this as
  // `compareDateRange: [ …ranges ]` on the time dimension INSTEAD of `dateRange`
  // (`TimeDimensionComparison`); the api-gateway then fans the query out into one
  // sub-query per entry, IN ARRAY ORDER, and returns one `results[]` entry each.
  // So `[ current, prior ]` in, `[ current, prior ]` out of `decompose()`.
  //
  // Requires a RESOLVED window: you cannot subtract a period from the string
  // "last 30 days". When the prior period is not computable (an unresolved
  // passthrough string, or a degenerate zero-width window) we fall back to a
  // plain `dateRange` — the widget then reports its delta as UNKNOWN ("—")
  // rather than fabricating a comparison against a period we never established.
  if ( dateRange.compare && Array.isArray( window )) {
    const prior = priorPeriodOf( window );
    if ( prior ) {
      const comparison: TimeDimensionComparison = {
        ...timeDimension,
        compareDateRange: [ window, prior ]
      };
      return comparison;
    }
  }

  timeDimension.dateRange = window;
  return timeDimension;
};

/**
 * Transform a validated builder-form {@link WidgetDefinition} into the Cube
 * wire {@link Query} that `cubeApi.load()` accepts. Builder-only keys (`cube`,
 * `metrics`, `dateRange`, `type`, `widgetComponentProps`) are intentionally
 * dropped, and empty collections are omitted rather than sent as `[]`.
 *
 * @param now Injected clock, for deterministic tests of the relative-range
 *   resolution. Defaults to the real current time.
 */
export const widgetDefinitionToCubeQuery = ( definition: WidgetDefinition, now?: Date ): Query => {
  const query: Query = {};

  if ( definition.measures?.length ) {
    query.measures = [...definition.measures];
  }

  const dimensions = ( definition.dimensions ?? [])
    .map( dimension => dimension.field )
    .filter(( field ): field is string => Boolean( field ));
  if ( dimensions.length ) {
    query.dimensions = dimensions;
  }

  const filters = ( definition.filters ?? [])
    .filter( filter => Boolean( filter.field ))
    .map( builderFilterToCubeFilter );
  if ( filters.length ) {
    query.filters = filters;
  }

  if ( definition.dateRange ) {
    query.timeDimensions = [builderDateRangeToTimeDimension( definition.dateRange, now )];
  }

  const order = ( definition.orderBy ?? [])
    .filter( entry => Boolean( entry.field ))
    .map(( entry ): [ string, QueryOrder ] => [ entry.field, entry.direction ]);
  if ( order.length ) {
    query.order = order;
  }

  if ( typeof definition.limit === 'number' ) {
    query.limit = definition.limit;
  }
  if ( typeof definition.offset === 'number' ) {
    query.offset = definition.offset;
  }

  return query;
};
